import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Sparkles, Mail, Lock, User } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const AuthDialog = ({ open, onOpenChange }) => {
  const { setUser, refreshUsage } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const { data } = await api.post(path, form);
      setUser(data.user);
      await refreshUsage();
      toast.success(mode === "login" ? "Connexion réussie" : "Compte créé, bienvenue !");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-extrabold flex items-center gap-2">
            <Sparkles className="text-[#00A3E0]" /> {mode === "login" ? "Connexion" : "Créer un compte"}
          </DialogTitle>
          <DialogDescription className="sr-only">Connexion ou création de compte ProbaLoto</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <Button onClick={google} variant="outline" className="w-full rounded-full h-11 font-bold" data-testid="google-login-btn">
            <img alt="g" src="https://www.google.com/favicon.ico" className="w-4 h-4 mr-2" /> Continuer avec Google
          </Button>
          <div className="flex items-center gap-3 text-xs text-slate-400 font-bold uppercase tracking-wider">
            <span className="h-px bg-slate-200 flex-1" /> ou <span className="h-px bg-slate-200 flex-1" />
          </div>
          {mode === "register" && (
            <div className="relative">
              <User className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <Input className="pl-9 rounded-xl h-11" placeholder="Nom" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-name-input" />
            </div>
          )}
          <div className="relative">
            <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
            <Input className="pl-9 rounded-xl h-11" placeholder="Email" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email-input" />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
            <Input className="pl-9 rounded-xl h-11" placeholder="Mot de passe" type="password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="auth-password-input" />
          </div>
          <Button onClick={submit} disabled={busy} className="w-full rounded-full h-11 font-bold text-white bg-[#0052CC] hover:bg-[#003B80]" data-testid="auth-submit-btn">
            {busy ? "..." : mode === "login" ? "Se connecter" : "Créer mon compte"}
          </Button>
          <p className="text-center text-sm text-slate-500">
            {mode === "login" ? "Pas encore de compte ?" : "Déjà inscrit ?"}{" "}
            <button className="font-bold text-[#00A3E0]" onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="auth-switch-mode">
              {mode === "login" ? "S'inscrire" : "Se connecter"}
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
